import React from 'react';
import { useForm } from 'react-form';
import { useHistory } from 'react-router-dom';
import FullStory from 'react-fullstory';
// @ts-expect-error -- legacy JS module without TypeScript declarations
import ErrorBoundary from '~/shared/ErrorBoundary';
import ConfirmationModal from '~/admin/shared/forms/ConfirmationModal';
import { useIntl } from 'react-intl';
import { usePopmenuConfig } from '../../../utils/withPopmenuConfig';
import SelfSignUpFormStepper from './SelfSignUpFormStepper';
import { SelfSignUpProvider } from './hooks/useSelfSignUpContext';
import type { SelfSignUpForm } from './types';
import { getPlanSteps } from './PlansStep';
import type { ProductPlan } from './productPlan';
import useSelfSignUpRecovery from './hooks/useSelfSignUpRecovery';

const useMessages = () => {
  const intl = useIntl();

  return {
    cancelButtonText: intl.formatMessage({ defaultMessage: 'Start fresh' }),
    confirmationMessage: intl.formatMessage({ defaultMessage: 'We found an unfinished sign up form. Would you like to continue where you left off?' }),
    proceedButtonText: intl.formatMessage({ defaultMessage: 'Continue where I left off' }),
    title: intl.formatMessage({ defaultMessage: 'Resume your sign up' }),
  };
};

type FormContentProps = {
  initialValues: Partial<SelfSignUpForm>;
  initialStepIndex: number;
  onSwitchPlan: (productPlan: ProductPlan, values: Partial<SelfSignUpForm>) => void;
};

const SelfSignUpContent = ({ initialValues, initialStepIndex, onSwitchPlan }: FormContentProps) => {
  const { Form } = useForm({ defaultValues: initialValues });
  const history = useHistory();
  const popmenuConfig = usePopmenuConfig();
  const planSteps = getPlanSteps(initialValues.productPlan ?? 'starter');
  const stepsOrder = planSteps.steps.map(s => s.name);

  const resetStepper = () => {
    history.push('/');
  };

  const handleFinish = () => {
    history.push('/');
  };

  return (
    <React.Fragment>
      <Form>
        <SelfSignUpProvider initialStep={stepsOrder[initialStepIndex]} onSwitchPlan={onSwitchPlan}>
          <SelfSignUpFormStepper
            onClose={resetStepper}
            onFinish={handleFinish}
            onCancel={resetStepper}
            initialStepIndex={initialStepIndex}
          />
        </SelfSignUpProvider>
      </Form>
      {popmenuConfig.fullStoryOrg && (
        <ErrorBoundary>
          <FullStory
            org={popmenuConfig.fullStoryOrg}
          />
        </ErrorBoundary>
      )}

    </React.Fragment>
  );
};

const SelfSignUpContainer = ({ productPlan = 'starter' }: { productPlan?: ProductPlan }) => {
  const messages = useMessages();
  const {
    formState,
    handleDeclineRecovery,
    handleRecover,
    handleSwitchPlan,
    savedData,
  } = useSelfSignUpRecovery(productPlan);

  return (
    <React.Fragment>
      {(formState || !savedData) && (
        <SelfSignUpContent
          key={productPlan}
          initialValues={formState?.initialValues ?? { productPlan }}
          initialStepIndex={formState?.initialStepIndex ?? 0}
          onSwitchPlan={handleSwitchPlan}
        />
      )}
      <ConfirmationModal
        open={!!savedData && !formState}
        onClose={handleDeclineRecovery}
        confirmationAction={handleRecover}
        formattedMessage={messages.confirmationMessage}
        cancelButtonText={messages.cancelButtonText}
        proceedButtonText={messages.proceedButtonText}
        title={messages.title}
      />
    </React.Fragment>
  );
};

export default SelfSignUpContainer;
